import { useEffect, useMemo, useState } from "react";
import { formatUSPhone } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import BranchDropDown from "src/Pages/Setting/Team/Invite/InviteModal/branchdropdown";
import AccountDropdown from "./AccountDropdown";
import PhoneNumberInput from "src/Components/Authentication/PhoneNumberInput";

function LocationAddModal({
  branches,
  accounts = [],
  customers = [],
  customer,
  onAddClicked,
  onUpateClicked,
  onCancelClicked,
}) {
  const currentUser = UserService.getCurrentUser();
  const [companyName, setCompanyName] = useState("");
  const [companyNameSuffix, setCompanyNameSuffix] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [address, setAddress] = useState("");
  const [yesButtonTitle, setYesButtonTitle] = useState("ADD");
  const [branch, setBranch] = useState(null);
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  const accountOptions = useMemo(() => {
    return accounts.filter((item) => item?.name);
  }, [accounts]);

  const customerPrefix = selectedAccount ? `${selectedAccount.name} - ` : "";

  const displayedCompanyName = accounts.length > 0
    ? `${customerPrefix}${companyNameSuffix}`
    : companyName;

  useEffect(() => {
    if (customer) {
      const matchedAccount = accountOptions.find((item) => item.id === customer.account?.id);

      if (matchedAccount) {
        const prefix = `${matchedAccount.name} - `;
        setSelectedAccount(matchedAccount);
        setCompanyNameSuffix(customer.companyName.slice(prefix.length));
        setCompanyName(customer.companyName);
      } else {
        const fallbackAccount = customer.account || null;
        const fallbackPrefix = fallbackAccount?.name ? `${fallbackAccount.name} - ` : "";

        setSelectedAccount(fallbackAccount);
        setCompanyName(customer.companyName || "");
        setCompanyNameSuffix(
          fallbackPrefix && customer.companyName?.startsWith(fallbackPrefix)
            ? customer.companyName.slice(fallbackPrefix.length)
            : customer.companyName || ""
        );
      }

      setPhone(formatUSPhone(customer.phone));
      setAddress(customer.address);
      setBranch(customer.branch);
      setContact(customer.contact);
      setEmail(customer.email);
      setYesButtonTitle("UPDATE");
    } else {
      setSelectedAccount(null);
      setCompanyName("");
      setCompanyNameSuffix("");
      setPhone("");
      setAddress("");
      setBranch(null);
      setContact("");
      setEmail("");
      setYesButtonTitle("ADD");
    }
  }, [accountOptions, customer]);

  const companyNameChanged = (event) => {
    setDuplicateError(false);
    if (accounts.length === 0) {
      setCompanyName(event.target.value);
      return;
    }

    setCompanyNameSuffix(event.target.value);
  };

  const addClicked = () => {
    setSubmitted(true);
    const finalCompanyName = accounts.length > 0
      ? `${customerPrefix}${companyNameSuffix}`.trim()
      : companyName.trim();

    if ((!finalCompanyName || (accounts.length > 0 && !selectedAccount && !customer)) || !branch) return;

    const normalizedName = finalCompanyName.toLowerCase();
    const isDuplicate = customers.some(
      (item) =>
        item.id !== customer?.id &&
        item.companyName?.trim().toLowerCase() === normalizedName
    );
    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }
    setDuplicateError(false);

    if (customer) {
      customer.companyName = finalCompanyName;
      customer.phone = phone;
      customer.address = address;
      customer.branch = branch;
      if (accounts.length > 0 && selectedAccount) {
        customer.account = selectedAccount;
      }
      customer.contact = contact;
      customer.email = email;
      onUpateClicked(customer);
    } else {
      const data = {
        companyName: finalCompanyName,
        phone,
        address,
        branch,
        contact,
        email,
        createdById: currentUser?.id,
        ...(accounts.length > 0 && selectedAccount ? { accountId: selectedAccount.id } : {}),
      };
      onAddClicked(data);
    }
  };

  const cancelClicked = () => {
    onCancelClicked();
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-[448px] bg-white rounded-[6px] p-[18px] flex flex-col">
        <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor mb-5">
          {customer
            ? accounts.length > 0
              ? "EDIT LOCATION"
              : "EDIT CUSTOMER"
            : accounts.length > 0
              ? "ADD LOCATION"
              : "ADD CUSTOMER"}
        </p>

        <div className="flex flex-col justify-between h-full">
          {accounts.length > 0 && !customer && (
            <div className="h-[60px] w-full mb-2">
              <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">
                Customer
              </p>
              <div className={submitted && !selectedAccount ? "border-red-500 rounded-[6px]" : ""}>
                <AccountDropdown
                  onSelect={(selectedOption) => {
                    if (!selectedOption) {
                      return;
                    }

                    setSelectedAccount(selectedOption);
                    setCompanyNameSuffix("");
                  }}
                />
              </div>
            </div>
          )}

          <div className="h-[60px] w-full mb-2">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">Location Name</p>
            <div className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center px-[15px] ${(submitted && !displayedCompanyName.trim()) || duplicateError ? "border-red-500" : ""}`}>
              {accounts.length > 0 && selectedAccount && (
                <span className="text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium whitespace-nowrap">
                  {customerPrefix}
                </span>
              )}
              <input
                value={accounts.length > 0 ? companyNameSuffix : displayedCompanyName}
                type="text"
                onChange={companyNameChanged}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder={
                  accounts.length > 0
                    ? selectedAccount
                      ? "Enter location name"
                      : customer
                        ? "Enter location name"
                        : "Select customer first"
                    : "Enter company name"
                }
                disabled={accounts.length > 0 && !selectedAccount && !customer}
              />
            </div>
            {duplicateError && (
              <p className="text-red-500 text-[10px] font-OpenSansMedium mt-1">
                A {accounts.length > 0 ? "location" : "customer"} with this name already exists.
              </p>
            )}
          </div>
          <div className="h-[60px] w-full mb-2">
            <PhoneNumberInput
              label="Phone Number"
              labelClassName="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1"
              value={phone}
              onChange={setPhone}
              onInvalidChange={setPhoneInvalid}
            />
          </div>

          <div className="h-[60px] w-full mb-2">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">Address</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={address}
                type="text"
                onChange={(e) => setAddress(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter Address"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mb-2">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">Contact Name</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={contact}
                type="text"
                onChange={(e) => setContact(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter contact name"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mb-2">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">Email Address</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter email address"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mb-2">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-1">Branch</p>
            <div className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center ${submitted && !branch ? "border-red-500" : ""}`}>
              <BranchDropDown branches={branches} selectedBranch={branch} branchClicked={setBranch} />
            </div>
          </div>

          <div className=" w-full">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={cancelClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={addClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] flex items-center justify-center bg-blueColor text-white cursor-pointer"
              >
                {yesButtonTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationAddModal;
